import { useEffect, useState, useCallback } from 'react';
import { useApiContext } from '../../../api';
import styles from '../Upload.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Asset } from '../../../api/models/assets';
import { TpAssetInfo, TpAssetRelationInfo } from '../../../data/assets/interfaces';
import { addNewAsset, modifyAsset } from '../../../data/assets/queriedAssetsSlice';
import { useAppDispatch } from '../../../app/hooks';
import { getRelationTypeforAssets } from '../../../data/assets/helpers';
import { UploadFileData } from '../../../api/models/data';
import { UploadFile, getFileAssetType } from '../../../components/files/helpers';

interface UploadProgressProps {
  ontology: any;
  collectionProperties: any;
  selectedTemplate: any;
  fileSetAssets: any;
  setCollectionAsset: (newValue: TpAssetInfo) => void;
  setUploadComplete: (newValue: boolean) => void;
  abortController: AbortController | null;
  uploadAborted: boolean;
  setUploadAborted: (newValue: boolean) => void;
}

// Upload 4 files concurrently to balance speed with browser connection limits
const BATCH_SIZE_UPLOAD = 4;
// Create 3 assets per batch to stay under API rate limits
const BATCH_SIZE_ASSET_CREATION = 3;
// 300ms delay between batches to avoid rate limiting
const DELAY_BETWEEN_BATCHES_MS = 300;

/**
 * Executes promises in batches to limit concurrency.
 * @param promises list of promises to execute
 * @param batchSize batch size
 * @param delay delay between batches in milliseconds
 * @returns array of resolved promise values
 */
async function batchPromiseAll<T>(
  promises: (() => Promise<T>)[],
  batchSize: number = -1,
  delay: number = 0,
): Promise<T[]> {
  const results: T[] = [];
  if (batchSize <= 0) {
    batchSize = Math.max(promises.length, 1);
  }
  for (let i = 0; i < promises.length; i += batchSize) {
    if (i > 0 && delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    const batch = promises.slice(i, i + batchSize);
    const execBatch = batch.map((p) => p());
    const batchResults = await Promise.all(execBatch);
    results.push(...batchResults);
  }
  return results;
}

export function UploadProgress(props: UploadProgressProps) {
  const {
    ontology,
    collectionProperties,
    selectedTemplate,
    fileSetAssets,
    setCollectionAsset,
    setUploadComplete,
    abortController,
    uploadAborted,
    setUploadAborted,
  } = props;
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const [fileUploadObjects, setFileUploadObjects] = useState<{ file: UploadFile; uploadData: UploadFileData }[]>([]);
  const [fileProgress, setFileProgress] = useState({});
  const [fileNameToObjMapper, setFileNameToObjMapper] = useState({});
  const [assetsCreated, setAssetsCreated] = useState(false);

  useEffect(() => {
    setUploadComplete(Object.values(fileProgress).every((v) => v === 1));
  }, [fileProgress]);

  const uploadFile = useCallback(
    (uploadData: { file: UploadFile; signedData: UploadFileData }) => {
      const { file, signedData } = uploadData;
      return fetch(signedData.signedUrl!, {
        method: signedData.method,
        body: file,
        headers: {
          'Content-Type': file.type, // Set the Content-Type based on file type
        },
        signal: abortController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to upload file');
          }
          setFileProgress((prevProgress) => ({ ...prevProgress, [uploadData.file.name]: 1 }));
          setFileNameToObjMapper((prevMapper) => ({ ...prevMapper, [uploadData.file.name]: uploadData.signedData }));
        })
        .catch((error) => {
          if (abortController.signal.aborted) {
            setUploadAborted(true);
            console.error('Upload aborted');
          } else {
            console.error('Error uploading file:', error);
          }
        });
    },
    [abortController, setUploadAborted],
  );

  useEffect(() => {
    // build list of upload items from fileSetAssets
    const items: { file: UploadFile; uploadData: UploadFileData }[] = [];
    Object.entries(fileSetAssets).forEach(([key, value]: [string, any]) => {
      Object.entries(value.files).forEach(([k, v]: [string, any]) => {
        // initialize progress
        setFileProgress((prev) => ({ ...prev, [v.selected.name]: 0 }));
        const uploadData: UploadFileData = {
          fileName: v.selected.name,
          method: 'PUT',
          fileType: getFileAssetType(selectedTemplate),
        };
        items.push({ file: v.selected, uploadData });
      });
    });
    setFileUploadObjects((prev) => [
      ...prev,
      ...items.map((item) => ({ file: item.file, uploadData: item.uploadData })),
    ]);

    if (items.length === 0) return;

    const uploadPromises: (() => Promise<Response | void>)[] = items.map((item) => {
      return () =>
        api.blob.getPresignedUrlForUpload(item.uploadData).then((signedData) => {
          return uploadFile({ file: item.file, signedData });
        });
    });

    (async () => {
      try {
        await batchPromiseAll(uploadPromises, BATCH_SIZE_UPLOAD, DELAY_BETWEEN_BATCHES_MS);
      } catch (error) {
        console.error('Batch upload error:', error);
        setUploadAborted(true);
      }
    })();
    return () => {
      abortController?.abort();
    };
  }, [fileSetAssets, api, selectedTemplate, uploadFile]);

  useEffect(() => {
    if (
      fileUploadObjects.length > 0 &&
      Object.keys(fileNameToObjMapper).length === fileUploadObjects.length &&
      Object.values(fileProgress).every((v) => v === 1) &&
      !assetsCreated
    ) {
      const collectionDisplayName = collectionProperties[selectedTemplate.collectionAssetDisplayNameProperty];
      const collectionAssetToCreate: Asset = {
        type: selectedTemplate.collectionAssetType,
        properties: collectionProperties,
        description: '',
        tags: [],
        name: collectionDisplayName.toLowerCase().replace(/\s+/g, '_'),
        displayName: collectionDisplayName,
      };

      api.asset
        .addAsset(collectionAssetToCreate)
        .then((createdAsset) => {
          if (selectedTemplate.collectionDataContext) {
            const propertyInfo = { key: [selectedTemplate.collectionDataContext], value: createdAsset.id };
            return api.asset.setAssetProperty(createdAsset.id, propertyInfo);
          } else {
            return createdAsset;
          }
        })
        .then((collectionAsset) => {
          dispatch(addNewAsset({ asset: collectionAsset }));
          const fileSetPromises: (() => Promise<any>)[] = [];
          for (const [key, value] of Object.entries(fileSetAssets)) {
            fileSetPromises.push(async () => {
              const properties = {};
              Object.entries(value).forEach(([k, v]) => {
                if (k !== 'files') {
                  properties[k] = v;
                }
              });
              const fileSetAssetToCreate: Asset = {
                type: selectedTemplate.fileSetAssetType,
                properties: properties,
                description: '',
                tags: [],
                name: key.toLowerCase().replace(/\s+/g, '_'),
                displayName: key,
              };
              let createdAsset = await api.asset.addAsset(fileSetAssetToCreate);
              if (selectedTemplate.fileSetAssetDataContext) {
                const propertyInfo = { key: [selectedTemplate.fileSetAssetDataContext], value: createdAsset.id };
                createdAsset = await api.asset.setAssetProperty(createdAsset.id, propertyInfo);
              }
              if (Object.keys(value).includes('files')) {
                const filesToUpload: UploadFileData[] = Object.entries(value.files).map(([k, v]) => {
                  return {
                    fileName: v.selected.name,
                    blobPath: fileNameToObjMapper[v.selected.name].blobPath,
                    destinationProperty: k,
                    fileType: getFileAssetType(selectedTemplate),
                  };
                });
                createdAsset = await api.asset.addFilesToAsset(createdAsset.id, filesToUpload);
              }
              dispatch(addNewAsset({ asset: createdAsset }));
              const relationType = getRelationTypeforAssets(ontology, collectionAsset.type, createdAsset.type);
              if (!relationType) {
                throw new Error(`No relation type found between ${collectionAsset.type} and ${createdAsset.type}`);
              }
              return api.relation.addRelation(collectionAsset.id, createdAsset.id, relationType, true);
            });
          }
          return batchPromiseAll(fileSetPromises, BATCH_SIZE_ASSET_CREATION, DELAY_BETWEEN_BATCHES_MS).then(
            (relations: TpAssetRelationInfo[]) => {
              const asset = { ...collectionAsset };
              for (const relation of relations) {
                const relationToAdd = {
                  id: relation.id,
                  to: relation.toAsset.id,
                  type: relation.type,
                };
                asset.relations = [...asset.relations, relationToAdd];
              }
              dispatch(modifyAsset({ asset }));
              setCollectionAsset(asset);
              setAssetsCreated(true);
            },
          );
        });
    }
  }, [fileNameToObjMapper, fileProgress]);

  return (
    <>
      <div className={styles.uploadInProgress}>
        {Object.values(fileProgress).every((v) => v === 1)
          ? 'Upload Completed'
          : uploadAborted
            ? 'Upload Aborted, go back to change upload'
            : 'Upload in Progress...'}
      </div>
      {Object.entries(fileSetAssets).map(([key, value]) => {
        return (
          <div key={key} style={{ marginLeft: '50px', borderBottom: '1px dotted black', width: '300px' }}>
            {Object.entries(value.files).map(([k, v]) => {
              return (
                <div key={`${k}-${key}`} style={{ marginBottom: 5, fontSize: '15px' }}>
                  <div style={{ display: 'inline-block', width: '250px' }}>{v.selected.name}</div>
                  <div style={{ display: 'inline-block', width: '50px' }}>
                    {fileProgress[v.selected.name] === 0 && !uploadAborted ? (
                      <FontAwesomeIcon className={styles.statusIcon} icon={['fad', 'loader']} spinPulse />
                    ) : uploadAborted && fileProgress[v.selected.name] === 0 ? (
                      <FontAwesomeIcon className={styles.abortIcon} icon={['fad', 'circle-exclamation']} />
                    ) : (
                      <FontAwesomeIcon className={styles.statusIcon} icon={['fad', 'circle-check']} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
